import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Drawer from '@mui/material/Drawer';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ViewResult from '../../../admin/component/ViewResult';
import '../../admin.css';
import {
  Eye, Edit, Download, Search, Filter, RefreshCw, User, Mail, FileText,
  ExternalLink, Trash2, Clock, CheckCircle, XCircle, AlertCircle, Users,
  Briefcase, X, Calendar, ChevronDown, Building, BarChart3, MoreVertical,
} from 'lucide-react';
import api, { deleteJobApplication, deleteJobDescription } from '../../../../api/client';
import { IoArchiveOutline } from 'react-icons/io5';
import { useSkillAnalysis } from '../../../../hooks/useCandidateDetailApis';
import SkillAnalysisModal from '../candidate/SkillAnalysisModal';
import Pagination from '../../component/Pagination';
import PremiumSelect from '../../component/PremiumSelect';
import PremiumDatePicker from '../../component/PremiumDatePicker';
import PageHeader, { PageCard, PageShell } from '../../../common/PageHeader';
import { usePageLoading } from '../../../../contexts/LoadingContext';

// Compact StatCard Component
const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl px-3.5 py-2 sm:py-2.5 shadow-2xs border border-gray-200/80 hover:shadow-xs transition-all flex items-center justify-between">
    <div className="min-w-0">
      <p className="text-[11px] font-medium text-gray-500 truncate">{title}</p>
      <p className={`text-base sm:text-lg font-bold leading-tight ${color}`}>{value}</p>
    </div>
    <div className={`p-2 rounded-lg shrink-0 ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
      <Icon size={16} className={color} />
    </div>
  </div>
);

// Status Badge Component

// Compact Status Badge Component
const StatusBadge = ({ status, statusDisplay }) => {
  const getStatusStyles = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'reviewed': 'bg-blue-100 text-blue-800 border-blue-200',
      'shortlisted': 'bg-green-100 text-green-800 border-green-200',
      'interview_scheduled': 'bg-purple-100 text-purple-800 border-purple-200',
      'interviewed': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'accepted': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200',
      'withdrawn': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return styles[status] || styles['pending'];
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs border ${getStatusStyles(status)}`}>
      {statusDisplay}
    </span>
  );
};

// Compact Application Table Component
const ApplicationTable = ({
  applications, onViewDetails, onUpdateStatus, onDeleteApplication,
  onViewCV, onDownloadCV, onViewResult
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = document.documentElement.dir === 'rtl' || i18n?.language === 'ar';

  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedActionApp, setSelectedActionApp] = useState(null);

  const handleOpenActionMenu = (event, app) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setSelectedActionApp(app);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedActionApp(null);
  };

  const handleMenuAction = (actionFn, ...args) => {
    handleCloseActionMenu();
    if (typeof actionFn === 'function') {
      actionFn(...args);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('jobApplications.nA', 'N/A');
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric'
    });
  };

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden lg:block">
        <table className="w-full min-w-[850px] bg-white divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10 shadow-xs">
            <tr>
              <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 w-[27%]">
                {t('jobApplications.candidate')}
              </th>
              <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 w-[25%]">
                {t('jobApplications.jobPosition')}
              </th>
              <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 w-[16%]">
                {t('jobApplications.status')}
              </th>
              <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 w-[14%]">
                {t('jobApplications.appliedDate')}
              </th>
              <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 w-[18%]">
                {t('jobApplications.interview')}
              </th>
              <th className="py-2.5 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 w-[60px]">
                {t('jobApplications.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {applications.map((application, index) => (
              <tr
                key={application.id}
                className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/40 transition-colors`}
              >
                {/* Candidate */}
                <td className="py-2.5 px-4 align-middle whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs shrink-0 shadow-2xs">
                      {application.candidate_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="ml-3 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {application.candidate_name}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center truncate">
                        <Mail size={12} className="mr-1 shrink-0 text-gray-400" />
                        <span className="truncate">{application.candidate_email}</span>
                      </div>
                    </div>
                  </div>
                </td>

                {/* Job Position */}
                <td className="py-2.5 px-4 align-middle whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 truncate">{application.job_title}</div>
                  <div className="text-xs text-gray-500 flex items-center truncate">
                    <Building size={12} className="mr-1 shrink-0 text-gray-400" />
                    <span className="truncate">{application.company_name}</span>
                  </div>
                </td>

                {/* Status */}
                <td className="py-2.5 px-4 align-middle whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={application.status} statusDisplay={application.status_display} />
                    <button
                      type="button"
                      onClick={() => onUpdateStatus(application)}
                      className="text-green-600 hover:text-green-800 transition-colors p-1 hover:bg-green-50 rounded cursor-pointer"
                      title={t('jobApplications.updateStatus')}
                    >
                      <Edit size={12} />
                    </button>
                  </div>
                </td>

                {/* Applied Date */}
                <td className="py-2.5 px-4 align-middle whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(application.applied_at)}</div>
                  <div className="text-xs text-gray-500">
                    {application.days_since_applied} {t('jobApplications.daysAgo')}
                  </div>
                </td>

                {/* Interview */}
                <td className="py-2.5 px-4 align-middle whitespace-nowrap">
                  {application.interview_status?.has_completed_interview ? (
                    <div className="flex flex-col items-start space-y-0.5">
                      <div
                        onClick={() => onViewResult(application)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full border border-green-200 cursor-pointer hover:bg-green-200 transition-colors text-xs font-medium"
                      >
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        <span>{t('jobApplications.interviewDone')}</span>
                      </div>
                      {application.last_interview_result && (
                        <div className="pl-2">
                          <span className={`text-xs font-bold ${
                            application.last_interview_result.total_score >= 80 ? 'text-emerald-600' :
                            application.last_interview_result.total_score >= 60 ? 'text-sky-600' :
                            application.last_interview_result.total_score >= 40 ? 'text-orange-600' : 'text-red-600'
                          }`}>
                            {application.last_interview_result.total_score ? application.last_interview_result.total_score.toFixed(1) : t('jobApplications.nA')}%
                          </span>
                        </div>
                      )}
                    </div>
                  ) : application.interview_status?.has_active_interview ? (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full border border-yellow-200 text-xs font-medium">
                      <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse"></div>
                      <span>{t('jobApplications.inProgress')}</span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full border border-gray-200 text-xs font-medium">
                      <Clock size={12} />
                      <span>{t('jobApplications.noInterview')}</span>
                    </div>
                  )}
                </td>

                {/* Actions (3-Dots Menu) */}
                <td className="py-2.5 px-4 align-middle text-center whitespace-nowrap">
                  <button
                    type="button"
                    onClick={(e) => handleOpenActionMenu(e, application)}
                    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer ${
                      selectedActionApp?.id === application.id && Boolean(actionMenuAnchor)
                        ? 'bg-blue-100 text-blue-700 shadow-xs'
                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                    title={t('jobApplications.actions', 'Actions')}
                    aria-label="Actions"
                  >
                    <MoreVertical size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-gray-200">
        {applications.map((application) => (
          <div key={application.id} className="p-3 hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs shrink-0">
                  {application.candidate_name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-medium text-gray-900 truncate">
                    {application.candidate_name}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">
                    {application.candidate_email}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={application.status} statusDisplay={application.status_display} />
                <button
                  type="button"
                  onClick={() => onUpdateStatus(application)}
                  className="text-green-600 hover:text-green-800 transition-colors p-1 hover:bg-green-50 rounded cursor-pointer"
                  title={t('jobApplications.updateStatus')}
                >
                  <Edit size={12} />
                </button>
              </div>
            </div>

            <div className="mb-2">
              <p className="text-sm font-medium text-gray-900">{application.job_title}</p>
              <p className="text-xs text-gray-500">{application.company_name}</p>
            </div>

            <div className="flex items-center justify-between pt-1 border-t border-gray-100">
              <div className="text-xs text-gray-500">
                {formatDate(application.applied_at)} ({application.days_since_applied} {t('jobApplications.daysAgo')})
              </div>
              <button
                type="button"
                onClick={(e) => handleOpenActionMenu(e, application)}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
              >
                <span>{t('jobApplications.actions', 'Actions')}</span>
                <MoreVertical size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 3-Dots Action Menu */}
      <Menu
        id="application-actions-menu"
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRTL ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRTL ? 'left' : 'right',
        }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              mt: 0.5,
              borderRadius: '12px',
              minWidth: '200px',
              border: '1px solid #f1f5f9',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
              py: 0.5,
              '& .MuiMenuItem-root': {
                px: 1.5,
                py: 1,
                fontSize: '0.8125rem',
                borderRadius: '8px',
                mx: 0.5,
                my: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#334155',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: '#f8fafc',
                  color: '#0f172a',
                },
              },
            },
          },
        }}
      >
        {/* 1. View Details */}
        <MenuItem onClick={() => handleMenuAction(onViewDetails, selectedActionApp)}>
          <Eye size={15} className="text-blue-600 shrink-0" />
          <span className="font-medium">{t('jobApplications.viewDetails', 'View Details')}</span>
        </MenuItem>

        {/* 2. Update Status */}
        <MenuItem onClick={() => handleMenuAction(onUpdateStatus, selectedActionApp)}>
          <Edit size={15} className="text-emerald-600 shrink-0" />
          <span className="font-medium">{t('jobApplications.updateStatus', 'Update Status')}</span>
        </MenuItem>

        {/* 3. View CV */}
        {(selectedActionApp?.resume || selectedActionApp?.resume_filename) && (
          <MenuItem onClick={() => handleMenuAction(onViewCV, selectedActionApp.resume)}>
            <ExternalLink size={15} className="text-purple-600 shrink-0" />
            <span className="font-medium">{t('jobApplications.viewCV', 'View CV')}</span>
          </MenuItem>
        )}

        {/* 4. Download CV */}
        {(selectedActionApp?.resume || selectedActionApp?.resume_filename) && (
          <MenuItem onClick={() => handleMenuAction(onDownloadCV, selectedActionApp.resume, selectedActionApp.candidate_name)}>
            <Download size={15} className="text-sky-600 shrink-0" />
            <span className="font-medium">{t('jobApplications.downloadCV', 'Download CV')}</span>
          </MenuItem>
        )}

        {/* 5. View Interview Results */}
        {(selectedActionApp?.interview_session_id || selectedActionApp?.interview_status?.has_completed_interview) && (
          <MenuItem onClick={() => handleMenuAction(onViewResult, selectedActionApp)}>
            <BarChart3 size={15} className="text-indigo-600 shrink-0" />
            <span className="font-medium">{t('jobApplications.viewInterviewResults', 'Interview Results')}</span>
          </MenuItem>
        )}

        {/* Divider */}
        <div className="my-1 border-t border-slate-100" />

        {/* 6. Archive */}
        <MenuItem
          onClick={() => handleMenuAction(onDeleteApplication, selectedActionApp)}
          sx={{
            '&:hover': { backgroundColor: '#fef2f2 !important', color: '#b91c1c !important' }
          }}
        >
          <IoArchiveOutline size={15} className="text-red-500 shrink-0" />
          <span className="font-medium text-red-600">{t('jobApplications.archiveApplication', 'Archive Application')}</span>
        </MenuItem>
      </Menu>
    </>
  );
};
// Compact Status Update Modal Component
const StatusUpdateModal = ({
  application, isOpen, onClose, onUpdate, updating,
  handleAnalyzeSkills, skillAnalysisLoading, selectedJDForAnalysis, selectedSkills
}) => {
  const { t } = useTranslation();
  const [newStatus, setNewStatus] = useState(application?.status || '');
  const [showInterviewConfirmation, setShowInterviewConfirmation] = useState(false);

  useEffect(() => {
    if (application) {
      setNewStatus(application.status);
    }
  }, [application]);

  const handleSubmit = () => {
    if (newStatus === 'interview_scheduled') {
      setShowInterviewConfirmation(true);
    } else {
      onUpdate(application.id, newStatus);
    }
  };

  const handleConfirmInterviewScheduling = () => {
    setShowInterviewConfirmation(false);
    onUpdate(application.id, newStatus);
  };

  const handleCancelInterviewScheduling = () => {
    setShowInterviewConfirmation(false);
    setNewStatus(application.status);
  };

  if (!isOpen || !application) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t('jobApplications.updateApplicationStatus')}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4">
          <div className="mb-4 space-y-2">
            <p className="text-sm text-gray-600">
              <span className="font-medium">{t('jobApplications.candidateLabel')}</span> {application.candidate_name}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">{t('jobApplications.positionLabel')}</span> {application.job_title}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">{t('jobApplications.currentStatus')}</span>
              <StatusBadge status={application.status} statusDisplay={application.status_display} />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('jobApplications.newStatus')}</label>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              disabled={updating}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="pending">{t('jobApplications.pendingReviewOption')}</option>
              <option value="reviewed">{t('jobApplications.reviewed')}</option>
              <option value="shortlisted">{t('jobApplications.shortlistedOption')}</option>
              <option value="interview_scheduled">{t('jobApplications.interviewScheduled')}</option>
              <option value="interviewed">{t('jobApplications.interviewed')}</option>
              <option value="accepted">{t('jobApplications.accepted')}</option>
              <option value="rejected">{t('jobApplications.rejectedOption')}</option>
              <option value="withdrawn">{t('jobApplications.withdrawn')}</option>
            </select>
          </div>

          {updating && (
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={updating}
            className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={updating || newStatus === application.status}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
          >
            <Edit size={14} className="mr-1" />
            {t('jobApplications.updateStatusButton')}
          </button>
        </div>
      </div>

      {/* Interview Scheduling Confirmation Modal */}
      {showInterviewConfirmation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10]">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('jobApplications.scheduleInterview')}</h2>
              <button
                onClick={handleCancelInterviewScheduling}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4">
              <div className="mb-4">
                <div className="flex items-start mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <Calendar size={16} className="text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold text-gray-900">{t('jobApplications.confirmInterviewScheduling')}</h3>
                    <p className="text-sm text-gray-600">{t('jobApplications.interviewSchedulingDescription')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-start">
                  <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                    <span className="text-white text-xs">i</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-blue-900 mb-2 text-sm">{t('jobApplications.whatWillHappen')}</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• {t('jobApplications.statusUpdatedToInterviewScheduled')}</li>
                      <li>• {t('jobApplications.inviteGenerated')}</li>
                      <li>• {t('jobApplications.emailSent')}</li>
                      <li>• {t('jobApplications.candidateAddedToPool')}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <h4 className="font-medium text-gray-900 mb-2 text-sm">{t('jobApplications.candidateDetails')}</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><span className="font-medium">{t('jobApplications.name')}</span> {application.candidate_name}</p>
                  <p><span className="font-medium">{t('jobApplications.position')}</span> {application.job_title}</p>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{t('jobApplications.currentStatusLabel')}</span>
                    <StatusBadge status={application.status} statusDisplay={application.status_display} />
                  </div>
                </div>
              </div>

              {/* Skill Analysis Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900 text-sm">{t('jobApplications.skillAnalysis')}</h4>
                    {selectedJDForAnalysis && selectedSkills[selectedJDForAnalysis.id]?.length > 0 && (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle size={12} />
                        <span className="text-xs font-medium">{t('jobApplications.skillsSelected')}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleAnalyzeSkills(
                      {
                        id: application.candidate,
                        firstName: application.candidate_name?.split(' ')[0] || '',
                        lastName: application.candidate_name?.split(' ').slice(1).join(' ') || '',
                        email: application.candidate_email
                      },
                      {
                        id: application.job_description,
                        title: application.job_title
                      }
                    )}
                    disabled={skillAnalysisLoading}
                    className="flex items-center justify-center gap-1 px-2 py-1 rounded text-xs hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-gray-700 border border-gray-300"
                  >
                    {skillAnalysisLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                        <span>{t('jobApplications.analyzing')}</span>
                      </>
                    ) : (
                      <>
                        <BarChart3 size={12} />
                        <span>
                          {selectedJDForAnalysis && selectedSkills[selectedJDForAnalysis.id]?.length > 0
                            ? t('jobApplications.updateSkills')
                            : t('jobApplications.analyzeSkills')
                          }
                        </span>
                      </>
                    )}
                  </button>
                </div>

                {selectedJDForAnalysis && selectedSkills[selectedJDForAnalysis.id]?.length > 0 ? (
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">
                      <p className="font-medium mb-2">{t('jobApplications.selectedSkillsForInterview')}</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedSkills[selectedJDForAnalysis.id].map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                      {t('jobApplications.skillsSelectedCount', { count: selectedSkills[selectedJDForAnalysis.id].length })}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">{t('jobApplications.analyzeSkillsDescription')}</p>
                )}
              </div>

              {updating && (
                <div className="flex justify-center mb-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600 text-sm">{t('jobApplications.schedulingInterview')}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={handleCancelInterviewScheduling}
                disabled={updating}
                className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmInterviewScheduling}
                disabled={updating}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50"
              >
                <Calendar size={14} className="mr-1" />
                {t('jobApplications.scheduleInterviewButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Compact Application Detail Modal Component
const ApplicationDetailModal = ({
  application, isOpen, onClose, onUpdateStatus, onViewCV, onDownloadCV
}) => {
  const { t } = useTranslation();
  if (!isOpen || !application) return null;

  const formatDate = (dateString) => {
    if (!dateString) return t('jobApplications.nA');
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatSalary = (salary) => {
    if (!salary) return t('jobApplications.notSpecified');
    return `$${Number(salary).toLocaleString()}`;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[999999]">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
              {application.candidate_name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">{t('jobApplications.applicationDetails')}</h2>
              <p className="text-sm text-gray-600 truncate">{application.candidate_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded transition-colors ml-4"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Candidate Information */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <User size={18} className="mr-2 text-blue-600" />
                {t('jobApplications.candidateInformation')}
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-600">{t('jobApplications.fullName')}</label>
                  <p className="text-sm text-gray-900 mt-1">{application.candidate_name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">{t('jobApplications.emailAddress')}</label>
                  <p className="text-sm text-gray-900 mt-1 break-all">{application.candidate_email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">{t('jobApplications.applicationDate')}</label>
                  <p className="text-sm text-gray-900 mt-1">{formatDate(application.applied_at)}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">{t('jobApplications.daysSinceApplied')}</label>
                  <p className="text-sm text-gray-900 mt-1">{application.days_since_applied} {t('jobApplications.days')}</p>
                </div>
              </div>
            </div>

            {/* Job Information */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <Briefcase size={18} className="mr-2 text-green-600" />
                {t('jobApplications.jobInformation')}
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-600">{t('jobApplications.positionLabel')}</label>
                  <p className="text-sm text-gray-900 mt-1">{application.job_title}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">{t('jobApplications.company')}</label>
                  <p className="text-sm text-gray-900 mt-1">{application.company_name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">{t('jobApplications.currentStatusLabel')}</label>
                  <div className="mt-1">
                    <StatusBadge status={application.status} statusDisplay={application.status_display} />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">{t('jobApplications.expectedSalaryLabel')}</label>
                  <p className="text-sm text-gray-900 mt-1">{formatSalary(application.expected_salary)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Resume Section */}
          {application.resume_filename && (
            <div className="mt-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <FileText size={18} className="mr-2 text-purple-600" />
                {t('jobApplications.resumeCV')}
              </h3>
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      <FileText size={18} className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 break-all">
                        {application.resume_filename}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {t('jobApplications.uploadedBy')} {application.candidate_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onViewCV(application.resume)}
                      className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm"
                    >
                      <ExternalLink size={14} className="mr-1" />
                      {t('jobApplications.viewCVButton')}
                    </button>
                    <button
                      onClick={() => onDownloadCV(application.resume, application.resume_filename)}
                      className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center text-sm"
                    >
                      <Download size={14} className="mr-1" />
                      {t('jobApplications.download')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cover Letter */}
          <div className="mt-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">{t('jobApplications.coverLetter')}</h3>
            <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {application.cover_letter || t('jobApplications.noCoverLetter')}
              </p>
            </div>
          </div>

          {/* Admin Notes */}
          {application.admin_notes && (
            <div className="mt-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">{t('jobApplications.adminNotes')}</h3>
              <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {application.admin_notes}
                </p>
              </div>
            </div>
          )}

          {/* Feedback */}
          {application.feedback_to_candidate && (
            <div className="mt-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">{t('jobApplications.feedbackToCandidate')}</h3>
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {application.feedback_to_candidate}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('jobApplications.close')}
          </button>
          <button
            onClick={() => {
              onClose();
              onUpdateStatus(application);
            }}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Edit size={14} className="mr-1" />
            {t('jobApplications.updateStatusButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Compact Delete Confirmation Modal Component
const DeleteConfirmationModal = ({
  isOpen, onClose, onConfirm, item, type, loading
}) => {
  const { t } = useTranslation();
  if (!isOpen || !item) return null;

  const formatDate = (dateString) => {
    if (!dateString) return t('jobApplications.nA');
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[999999]">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Trash2 size={18} className="text-red-600" />
            <h2 className="text-lg font-semibold text-red-600">{t('jobApplications.confirmArchive')}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4">
          {type === 'application' && (
            <>
              <p className="text-sm text-gray-900 mb-3">
                {t('jobApplications.archiveApplicationMessage')}
              </p>
              <div className="space-y-1 text-sm text-gray-600">
                <p><span className="font-medium">{t('jobApplications.candidateLabel')}</span> {item.candidate_name}</p>
                <p><span className="font-medium">{t('jobApplications.positionLabel')}</span> {item.job_title}</p>
                <p><span className="font-medium">{t('jobApplications.appliedLabel')}</span> {formatDate(item.applied_at)}</p>
              </div>
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  {t('jobApplications.archiveNotification')}
                </p>
              </div>
            </>
          )}

          {type === 'job' && (
            <>
              <p className="text-sm text-gray-900 mb-3">
                {t('jobApplications.archiveJobDescription')}
              </p>
              <div className="space-y-1 text-sm text-gray-600 mb-3">
                <p><span className="font-medium">{t('jobApplications.positionLabel')}</span> {item.title}</p>
                <p><span className="font-medium">{t('jobApplications.company')}</span> {item.department?.company?.name}</p>
                <p><span className="font-medium">{t('jobApplications.applicationsCount')}</span> {item.application_count || 0}</p>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm mb-2">
                  {t('jobApplications.archiveWarning')}
                </p>
                <ul className="text-red-800 text-sm space-y-1 ml-4">
                  <li>• {t('jobApplications.jobDescription')}</li>
                  <li>• {t('jobApplications.allApplications')}</li>
                  <li>• {t('jobApplications.allRelatedData')}</li>
                </ul>
                <p className="text-red-800 text-sm mt-2">
                  {t('jobApplications.candidatesNotified')}
                </p>
              </div>
            </>
          )}

          {loading && (
            <div className="flex justify-center mt-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center disabled:opacity-50"
          >
            <IoArchiveOutline size={14} className="mr-1" />
            {t('jobApplications.archiveButton')}
          </button>
        </div>
      </div>
    </div>
  );
};
// Main Component
export default function JobApplications({ adminToken, onLogout }) {
  const { t, i18n } = useTranslation();
  const isRTL = document.documentElement.dir === 'rtl' || i18n?.language === 'ar';
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  // Skill analysis hook
  const {
    skillAnalysis, setSkillAnalysis, analyzeSkills, reanalyzeSkills
  } = useSkillAnalysis();

  // State management
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageLoading(loading, {
    message: t('jobApplications.loadingApplications', 'Loading applications...'),
  });
  const [error, setError] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Skill analysis state
  const [selectedSkills, setSelectedSkills] = useState({});
  const [showSkillAnalysisModal, setShowSkillAnalysisModal] = useState(false);
  const [selectedJDForAnalysis, setSelectedJDForAnalysis] = useState(null);
  const [skillAnalysisLoading, setSkillAnalysisLoading] = useState(false);

  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ViewResult modal states
  const [showResultModal, setShowResultModal] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);
  const [resultError, setResultError] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Notification state
  const [notification, setNotification] = useState({
    show: false, message: '', type: 'success'
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [interviewFilter, setInterviewFilter] = useState('');
  const [jdFilter, setJdFilter] = useState('');
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Options for PremiumSelect dropdowns inside Drawer
  const statusOptions = useMemo(() => [
    { value: '', label: t('jobApplications.allStatuses', 'All Statuses') },
    { value: 'pending', label: t('jobApplications.pendingReviewOption', 'Pending Review') },
    { value: 'reviewed', label: t('jobApplications.reviewed', 'Reviewed') },
    { value: 'shortlisted', label: t('jobApplications.shortlistedOption', 'Shortlisted') },
    { value: 'interview_scheduled', label: t('jobApplications.interviewScheduled', 'Interview Scheduled') },
    { value: 'interviewed', label: t('jobApplications.interviewed', 'Interviewed') },
    { value: 'accepted', label: t('jobApplications.accepted', 'Accepted') },
    { value: 'rejected', label: t('jobApplications.rejectedOption', 'Rejected') },
    { value: 'withdrawn', label: t('jobApplications.withdrawn', 'Withdrawn') },
  ], [t]);

  const jdOptions = useMemo(() => [
    { value: '', label: t('jobApplications.allJDs', 'All Job Descriptions') },
    ...jobDescriptions.map((jd) => ({
      value: jd.id.toString(),
      label: jd.title || `JD #${jd.id}`,
    }))
  ], [jobDescriptions, t]);

  const interviewOptions = useMemo(() => [
    { value: '', label: t('jobApplications.allInterviews', 'All Interviews') },
    { value: 'interview', label: t('jobApplications.hasInterview', 'Has Interview') },
    { value: 'no_interview', label: t('jobApplications.noInterview', 'No Interview') },
  ], [t]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter) count++;
    if (jdFilter) count++;
    if (interviewFilter) count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    return count;
  }, [statusFilter, jdFilter, interviewFilter, dateFrom, dateTo]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showAdvancedFilters) {
        setShowAdvancedFilters(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAdvancedFilters]);

  // Pagination States
  const initialPage = parseInt(localStorage.getItem("page")) || 1;
  const initialPageSize = parseInt(localStorage.getItem("page_size")) || 10;
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalApplications, setTotalApplications] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    localStorage.setItem("page", page.toString());
    localStorage.setItem("page_size", pageSize.toString());
  }, [page, pageSize]);

  // Statistics state
  const [stats, setStats] = useState({
    total: 0, pending: 0, shortlisted: 0, rejected: 0
  });


  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (interviewFilter) params.append('interview_filter', interviewFilter);
      if (jdFilter) params.append('job_id', jdFilter);

      const response = await api.get(`job-applications/company_applications/?${params}`);

      const data = response.data;
      if (data.results) {
        setApplications(data.results);
        setTotalApplications(data.count || 0);
        setTotalPages(Math.ceil((data.count || 0) / pageSize));
      } else {
        const apps = Array.isArray(data) ? data : [];
        setTotalApplications(apps.length);
        setTotalPages(Math.ceil(apps.length / pageSize));

        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setApplications(apps.slice(startIndex, endIndex));
      }

    } catch (err) {
      console.error('Error fetching applications:', err);
      setError(t('jobApplications.failedToLoadApplications'));
      setApplications([]);
      setTotalApplications(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, searchTerm, dateFrom, dateTo, interviewFilter, jdFilter, t]);

  useEffect(() => {
    fetchApplications();
    fetchStatistics();
  }, [fetchApplications]);

  useEffect(() => {
    const fetchJobDescriptions = async () => {
      try {
        const res = await api.get('job-descriptions/list_with_pagination/', {
          params: { page_size: 500, page: 1 }
        });
        const data = res.data?.results ?? res.data;
        setJobDescriptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching job descriptions for filter:', err);
        setJobDescriptions([]);
      }
    };
    fetchJobDescriptions();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await api.get('application-statistics/');
      const data = response.data;

      setStats({
        total: data.total_applications || 0,
        pending: data.status_breakdown?.find(s => s.status === 'pending')?.count || 0,
        shortlisted: data.status_breakdown?.find(s => s.status === 'shortlisted')?.count || 0,
        rejected: data.status_breakdown?.find(s => s.status === 'rejected')?.count || 0
      });
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 5000);
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      setUpdatingStatus(true);

      const requestData = { status: newStatus };

      if (newStatus === 'interview_scheduled' && selectedJDForAnalysis) {
        const jdId = selectedJDForAnalysis.id;
        const skillsForJD = selectedSkills[jdId] || [];
        if (skillsForJD.length > 0) {
          requestData.selected_skills = skillsForJD;
        }
      }

      const response = await api.post(`job-applications/${applicationId}/update_status/`, requestData);

      await fetchApplications();
      await fetchStatistics();

      setStatusModalOpen(false);
      setSelectedApplication(null);
      handleCloseSkillAnalysis();

      if (newStatus === 'interview_scheduled' && response.data.invite_info) {
        const inviteInfo = response.data.invite_info;

        if (inviteInfo.error) {
          showNotification(t('jobApplications.statusUpdatedInviteFailed', { error: inviteInfo.error }), 'warning');
        } else {
          const inviteAction = inviteInfo.invite_created ? t('jobApplications.statusUpdatedInviteCreated') : t('jobApplications.statusUpdatedInviteUsed');
          showNotification(inviteAction, 'success');
        }
      } else {
        showNotification(t('jobApplications.applicationStatusUpdated'));
      }

    } catch (err) {
      console.error('Error updating status:', err);
      showNotification(t('jobApplications.failedToUpdateStatus'), 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setDetailModalOpen(true);
  };

  const handleUpdateStatus = (application) => {
    setSelectedApplication(application);
    setStatusModalOpen(true);
  };

  const handleDeleteApplication = (application) => {
    setDeletingItem(application);
    setDeleteType('application');
    setDeleteModalOpen(true);
  };

  const handleAnalyzeSkills = async (candidate, jobDescription) => {
    const jdForAnalysis = {
      id: jobDescription.id || jobDescription,
      title: jobDescription.title || 'Job Position'
    };

    setSelectedJDForAnalysis(jdForAnalysis);
    setShowSkillAnalysisModal(true);
    setSkillAnalysisLoading(true);

    try {
      if (!candidate || !jobDescription) {
        throw new Error('Missing candidate or job description');
      }

      const candidateId = candidate.id || candidate;
      const jdId = jobDescription.id || jobDescription;
      const data = await analyzeSkills(candidateId, jdId);

      setSkillAnalysis(data);
      showNotification(t('jobApplications.skillAnalysisCompleted'), 'success');
    } catch (error) {
      console.error('❌ Error analyzing skills:', error);
      showNotification(t('jobApplications.failedToAnalyzeSkills'), 'error');
      setShowSkillAnalysisModal(false);
      setSelectedJDForAnalysis(null);
    } finally {
      setSkillAnalysisLoading(false);
    }
  };

  const handleClearCachesAndRestart = async () => {
    if (!selectedApplication?.candidate || !selectedJDForAnalysis?.id) return;
    const candidate = selectedApplication.candidate;
    const candidateId = candidate.id || candidate;
    const jdId = selectedJDForAnalysis.id;
    setSkillAnalysisLoading(true);
    try {
      const data = await analyzeSkills(candidateId, jdId);
      setSkillAnalysis(data);
      showNotification(t('jobApplications.skillAnalysisCompleted'), 'success');
    } catch (error) {
      console.error('Error re-analyzing after cache clear:', error);
      showNotification(t('jobApplications.failedToAnalyzeSkills'), 'error');
    } finally {
      setSkillAnalysisLoading(false);
    }
  };

  const handleApplySkillsAndReturn = () => {
    setShowSkillAnalysisModal(false);
    setSkillAnalysis(null);
  };

  const handleCloseSkillAnalysis = () => {
    setShowSkillAnalysisModal(false);
    setSelectedJDForAnalysis(null);
    setSkillAnalysis(null);
    setSelectedSkills({});
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const hasActiveFilters = () => {
    return statusFilter || searchTerm || dateFrom || dateTo || interviewFilter || jdFilter;
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);

      if (deleteType === 'application') {
        await deleteJobApplication(deletingItem.id);
        showNotification(t('jobApplications.applicationArchived'));
      } else if (deleteType === 'job') {
        await deleteJobDescription(deletingItem.id);
        showNotification(t('jobApplications.jobDescriptionArchived'));
      }

      await fetchApplications();
      await fetchStatistics();

      setDeleteModalOpen(false);
      setDeletingItem(null);
      setDeleteType('');

    } catch (error) {
      console.error('Error deleting item:', error);
      showNotification(error.response?.data?.error || t('jobApplications.failedToArchive'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setDeletingItem(null);
    setDeleteType('');
  };

  const handleViewCV = async (resumeId) => {
    try {
      if (!resumeId) {
        showNotification(t('jobApplications.noResumeId'), 'error');
        return;
      }

      const response = await api.get(`resumes/${resumeId}/download/`, {
        responseType: 'blob'
      });

      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      window.open(url, '_blank');

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

    } catch (err) {
      console.error('Error viewing CV:', err);
      showNotification(t('jobApplications.failedToViewCV', { error: err.response?.data?.error || err.message }), 'error');
    }
  };

  const handleDownloadCV = async (resumeId, filename) => {
    try {
      if (!resumeId) {
        showNotification(t('jobApplications.noResumeId'), 'error');
        return;
      }

      const response = await api.get(`resumes/${resumeId}/download/`, {
        responseType: 'blob'
      });

      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification(t('jobApplications.cvDownloaded'));

    } catch (err) {
      console.error('Error downloading CV:', err);
      showNotification(t('jobApplications.failedToDownloadCV', { error: err.response?.data?.error || err.message }), 'error');
    }
  };

  const handleViewResult = async (application) => {
    const sessionId = application.last_interview_result?.session_id || application.interview_session_id;

    if (!sessionId) {
      showNotification(t('jobApplications.noInterviewSession'), 'error');
      return;
    }

    setSelectedApplication(application);
    setShowResultModal(true);
    setLoadingResult(true);
    setResultError('');
    setFeedback(null);
    setSessionData(null);

    try {
      const sessionRes = await api.get(`sessions/${sessionId}/`);
      const feedbackRes = await api.get(`feedback/?session=${sessionId}`);

      if (!feedbackRes.data?.results?.length) {
        setResultError(t('jobApplications.noInterviewResults'));
        return;
      }

      setSessionData(sessionRes.data);
      setFeedback(feedbackRes.data.results[0]);

    } catch (error) {
      console.error('Error loading interview results:', error);
      setResultError(t('jobApplications.failedToLoadResults'));
    } finally {
      setLoadingResult(false);
    }
  };

  const handleApplyFilters = () => {
    fetchApplications();
    setPage(1);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setInterviewFilter('');
    setJdFilter('');
    setPage(1);
  };

  const handleClearAll = () => {
    setSearchTerm('');
    handleClearFilters();
  };

  return (
    <PageShell className="bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30">
      <PageCard>
        <PageHeader
          title={t('jobApplications.pageTitle')}
          subtitle={t('jobApplications.pageSubtitle')}
        />

        {/* Compact Statistics Cards Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 shrink-0 px-3 pt-3 sm:px-4">
          <StatCard
            title={t('jobApplications.totalApplications')}
            value={stats.total}
            icon={Users}
            color="text-blue-600"
          />
          <StatCard
            title={t('jobApplications.pendingReview')}
            value={stats.pending}
            icon={Clock}
            color="text-yellow-600"
          />
          <StatCard
            title={t('jobApplications.shortlisted')}
            value={stats.shortlisted}
            icon={CheckCircle}
            color="text-green-600"
          />
          <StatCard
            title={t('jobApplications.rejected')}
            value={stats.rejected}
            icon={XCircle}
            color="text-red-600"
          />
        </div>

        {/* Compact Front Filter Bar */}
        <div className="px-3 sm:px-4 py-2 border-b border-gray-100 shrink-0">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('jobApplications.searchCandidates', 'Search candidates...')}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-8 h-8.5 sm:h-9 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white placeholder-gray-400 text-gray-800 transition-all outline-none shadow-2xs hover:border-gray-400"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setPage(1);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded cursor-pointer"
                  title="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Actions: Filter Button & Refresh Button */}
            <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
              {/* Filter Button */}
              <button
                type="button"
                onClick={() => setShowAdvancedFilters(true)}
                className={`inline-flex items-center gap-1.5 px-3 h-8.5 sm:h-9 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer border shadow-2xs hover:shadow-xs ${
                  activeFilterCount > 0 || showAdvancedFilters
                    ? 'bg-blue-50 border-blue-400 text-blue-700 shadow-xs'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-600'
                }`}
                title={t('jobApplications.filters', 'Advanced Filters')}
              >
                <Filter size={14} className={activeFilterCount > 0 ? 'text-blue-600' : 'text-gray-500'} />
                <span>{t('jobApplications.filters', 'Filters')}</span>
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-blue-600 rounded-full shadow-xs">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Clear filters shortcut if active */}
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center justify-center w-8.5 h-8.5 sm:w-9 sm:h-9 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors cursor-pointer shadow-2xs"
                  title={t('jobApplications.clearFilters', 'Clear advanced filters')}
                  aria-label="Clear advanced filters"
                >
                  <X size={15} className="stroke-[2.5]" />
                </button>
              )}

              {/* Refresh Button */}
              <button
                type="button"
                onClick={() => {
                  fetchApplications();
                  fetchStatistics();
                }}
                disabled={loading}
                className="inline-flex items-center justify-center w-8.5 h-8.5 sm:w-9 sm:h-9 rounded-lg border border-gray-300 bg-white text-gray-600 hover:text-blue-600 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-2xs cursor-pointer disabled:opacity-50"
                title={t('jobApplications.refresh', 'Refresh')}
              >
                <RefreshCw size={15} className={loading ? 'animate-spin text-blue-600' : ''} />
              </button>
            </div>
          </div>
        </div>

        {/* Advanced Filters Slide-Over Drawer */}
        <Drawer
          anchor={isRTL ? 'left' : 'right'}
          open={Boolean(showAdvancedFilters)}
          onClose={() => setShowAdvancedFilters(false)}
          transitionDuration={{ enter: 350, exit: 250 }}
          SlideProps={{
            easing: {
              enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
              exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
            },
          }}
          sx={{
            zIndex: 1350,
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              backdropFilter: 'blur(2px)',
            },
            '& .MuiDrawer-paper': {
              width: { xs: '100%', sm: '440px' },
              maxWidth: '100%',
              boxSizing: 'border-box',
              background: '#ffffff',
              boxShadow: isRTL
                ? '10px 0 40px rgba(0, 0, 0, 0.18)'
                : '-10px 0 40px rgba(0, 0, 0, 0.18)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          }}
        >
          {/* Drawer Header with Blue Gradient */}
          <div
            className="flex items-center justify-between px-6 py-4 shadow-sm"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-xs flex items-center justify-center text-white shrink-0">
                <Filter size={18} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white tracking-tight leading-tight">
                  {t('jobApplications.advancedFilters', 'Advanced Filters')}
                </h2>
                <p className="text-[11px] text-blue-100">
                  {t('jobApplications.refineResults', 'Filter and refine candidate applications')}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowAdvancedFilters(false)}
              className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Drawer Body - Scrollable Fields */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
            {/* 1. Status Filter */}
            <PremiumSelect
              label={t('jobApplications.status', 'Application Status')}
              icon={CheckCircle}
              iconColor="text-emerald-600"
              value={statusFilter}
              onChange={(val) => {
                setStatusFilter(val);
                setPage(1);
              }}
              options={statusOptions}
              placeholder={t('jobApplications.allStatuses', 'All Statuses')}
              searchable={true}
              className="w-full"
              menuWidth="w-full"
            />

            {/* 2. Job Description Filter */}
            <PremiumSelect
              label={t('jobApplications.jobPosition', 'Job Position')}
              icon={Briefcase}
              iconColor="text-blue-600"
              value={jdFilter}
              onChange={(val) => {
                setJdFilter(val);
                setPage(1);
              }}
              options={jdOptions}
              placeholder={t('jobApplications.allJDs', 'All Job Descriptions')}
              searchable={true}
              className="w-full"
              menuWidth="w-full"
            />

            {/* 3. Interview Status Filter */}
            <PremiumSelect
              label={t('jobApplications.interviewStatus', 'Interview Status')}
              icon={Users}
              iconColor="text-indigo-600"
              value={interviewFilter}
              onChange={(val) => {
                setInterviewFilter(val);
                setPage(1);
              }}
              options={interviewOptions}
              placeholder={t('jobApplications.allInterviews', 'All Interviews')}
              searchable={true}
              className="w-full"
              menuWidth="w-full"
            />

            {/* 4. Date Range Filter */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-1.5 select-none">
                <Calendar size={13} className="text-violet-600" />
                <span>{t('jobApplications.appliedDateRange', 'Applied Date Range')}</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-gray-500 mb-1 block">{t('jobApplications.dateFrom', 'From')}</label>
                  <PremiumDatePicker
                    value={dateFrom}
                    max={dateTo && dateTo < todayStr ? dateTo : todayStr}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && val > todayStr) {
                        setDateFrom(todayStr);
                      } else {
                        setDateFrom(val);
                      }
                      setPage(1);
                    }}
                    placeholder="dd-mm-yyyy"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 mb-1 block">{t('jobApplications.dateTo', 'To')}</label>
                  <PremiumDatePicker
                    value={dateTo}
                    min={dateFrom || ''}
                    max={todayStr}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && val > todayStr) {
                        setDateTo(todayStr);
                      } else {
                        setDateTo(val);
                      }
                      setPage(1);
                    }}
                    placeholder="dd-mm-yyyy"
                    align="right"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Drawer Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
            <button
              type="button"
              onClick={handleClearFilters}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            >
              {t('jobApplications.clearFilters', 'Clear filters')}
            </button>
            <button
              type="button"
              onClick={() => setShowAdvancedFilters(false)}
              className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm cursor-pointer"
            >
              {t('jobApplications.showResults', {
                count: totalApplications,
                defaultValue: `Show ${totalApplications} results`
              })}
            </button>
          </div>
        </Drawer>

        {/* Error Alert */}
        {error && (
          <div className="mx-3 sm:mx-4 mt-2 bg-red-50 border border-red-200 rounded-lg p-2.5 flex items-start shrink-0">
            <AlertCircle size={16} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 text-xs sm:text-sm">{error}</p>
          </div>
        )}

        {/* Table - Fills remaining viewport space without whole-page scroll */}
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
            {applications.length === 0 ? (
              loading ? null : (
              <div className="text-center py-12 flex flex-col items-center justify-center h-full">
                <div className="mx-auto mb-3 w-14 h-14 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center shadow-xs">
                  <Users size={24} className="text-blue-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">{t('jobApplications.noApplicationsFound')}</h3>
                <p className="text-gray-600 mb-3 text-xs sm:text-sm">
                  {hasActiveFilters()
                    ? t('jobApplications.noApplicationsMatchFilters')
                    : t('jobApplications.noApplicationsSubmitted')
                  }
                </p>
                {hasActiveFilters() && (
                  <button
                    onClick={handleClearFilters}
                    className="px-4 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-xs text-xs cursor-pointer"
                  >
                    {t('jobApplications.clearFilters')}
                  </button>
                )}
              </div>
              )
            ) : (
              <ApplicationTable
                applications={applications}
                onViewDetails={handleViewDetails}
                onUpdateStatus={handleUpdateStatus}
                onDeleteApplication={handleDeleteApplication}
                onViewCV={handleViewCV}
                onDownloadCV={handleDownloadCV}
                onViewResult={handleViewResult}
              />
            )}
          </div>

          {/* Pagination pinned flush at the bottom of the table card */}
          <div className="bg-white border-t border-gray-200 z-10 rounded-b-xl shrink-0">
            <Pagination
              currentPage={page}
              totalPages={totalPages || 1}
              totalCount={totalApplications}
              currentCount={applications.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSize={pageSize}
              showPageSize={false}
              hasActiveFilters={hasActiveFilters()}
              fixed={false}
              className="py-0"
            />
          </div>
        </div>
      </PageCard>

        {/* Modals */}
        <ApplicationDetailModal
          application={selectedApplication}
          isOpen={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          onUpdateStatus={handleUpdateStatus}
          onViewCV={handleViewCV}
          onDownloadCV={handleDownloadCV}
        />

        <StatusUpdateModal
          application={selectedApplication}
          isOpen={statusModalOpen}
          onClose={() => {
            setStatusModalOpen(false);
            handleCloseSkillAnalysis();
          }}
          onUpdate={handleStatusUpdate}
          updating={updatingStatus}
          handleAnalyzeSkills={handleAnalyzeSkills}
          skillAnalysisLoading={skillAnalysisLoading}
          selectedJDForAnalysis={selectedJDForAnalysis}
          selectedSkills={selectedSkills}
        />

        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          item={deletingItem}
          type={deleteType}
          loading={deleting}
        />

        {/* Skill Analysis Modal */}
        <SkillAnalysisModal
          isOpen={showSkillAnalysisModal}
          onClose={handleApplySkillsAndReturn}
          selectedCandidate={selectedApplication?.candidate}
          selectedJDForAnalysis={selectedJDForAnalysis}
          skillAnalysis={skillAnalysis}
          skillAnalysisLoading={skillAnalysisLoading}
          selectedSkills={selectedSkills}
          setSelectedSkills={setSelectedSkills}
          onApplySkillsAndReturn={handleApplySkillsAndReturn}
          reanalyzeSkills={reanalyzeSkills}
          onClearCachesAndRestart={handleClearCachesAndRestart}
        />

        {/* ViewResult Modal */}
        {showResultModal && (
          <ViewResult
            feedback={feedback}
            loadingFeedback={loadingResult}
            feedbackError={resultError}
            onClose={() => {
              setShowResultModal(false);
              setFeedback(null);
              setSessionData(null);
              setResultError('');
            }}
            sessionData={sessionData}
            isSendingEmail=""
            emailSent=""
            sendEmail=""
            callFrom="jobApplications"
          />
        )}

        {/* Notification Toast */}
        {notification.show && (
          <div className="fixed top-20 right-4 z-[99999] max-w-sm w-full mx-4 sm:mx-0">
            <div className={`rounded-lg p-3 shadow-lg border animate-in slide-in-from-right duration-300 ${notification.type === 'success'
              ? 'bg-green-100 border-green-200'
              : 'bg-red-100 border-red-200'
              }`}>
              <div className="flex items-start">
                {notification.type === 'success' ? (
                  <CheckCircle size={18} className="text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle size={18} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                    }`}>
                    {notification.message}
                  </p>
                </div>
                <button
                  onClick={() => setNotification({ show: false, message: '', type: 'success' })}
                  className="ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
    </PageShell>
  );
}